/**
 * Super-admin Subscription manager.
 * One row per hostel + school (auto-joined server-side). Status toggle is
 * the primary action — flipping to 'disabled' blocks login (402) on the next
 * login attempt by that org's admin.
 */
import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, FloppyDisk, Power, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) => n ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n) : "—";

const STATUS_STYLES = {
  active: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
  trial: "border-[var(--vs-secondary)]/40 text-[var(--vs-secondary)] bg-[var(--vs-secondary)]/10",
  disabled: "border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10",
};

export default function Subscriptions() {
  const [rows, setRows] = useState([]);
  const refresh = () => api.get("/admin/subscriptions").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const hostels = useMemo(() => rows.filter((r) => r.org_type === "hostel"), [rows]);
  const schools = useMemo(() => rows.filter((r) => r.org_type === "school"), [rows]);
  const disabledCount = rows.filter((r) => r.status === "disabled").length;
  const totalCollected = rows.reduce((s, r) => s + (r.last_payment_amount || 0), 0);

  return (
    <DashboardLayout title="Subscriptions" subtitle="Enable or disable each hostel & school based on payment status. Disabled workspaces cannot log in.">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Hostels" value={hostels.length} icon={<CreditCard size={11} />} testid="subs-stat-hostels" />
        <Stat label="Schools" value={schools.length} icon={<CreditCard size={11} />} testid="subs-stat-schools" />
        <Stat label="Disabled" value={disabledCount} icon={<Power size={11} />} testid="subs-stat-disabled" />
        <Stat label="Last 30d collections" value={fmtINR(totalCollected)} testid="subs-stat-collections" />
      </div>
      <Tabs defaultValue="hostels">
        <TabsList>
          <TabsTrigger value="hostels" data-testid="subs-tab-hostels">Hostels ({hostels.length})</TabsTrigger>
          <TabsTrigger value="schools" data-testid="subs-tab-schools">Schools ({schools.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="hostels"><SubsTable rows={hostels} onChanged={refresh} /></TabsContent>
        <TabsContent value="schools"><SubsTable rows={schools} onChanged={refresh} /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function Stat({ label, value, icon, testid }) {
  return (
    <div className="vs-card p-4" data-testid={testid}>
      <div className="eyebrow flex items-center gap-1.5">{icon}{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function SubsTable({ rows, onChanged }) {
  if (!rows.length) return <div className="vs-card p-10 text-center text-[var(--vs-text-secondary)]">Nothing yet.</div>;
  return (
    <div className="vs-card overflow-x-auto" data-testid="subs-table">
      <table className="w-full text-sm">
        <thead className="bg-[var(--vs-bg-subtle)]">
          <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
            <th className="px-4 py-3">Workspace</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Paid until</th>
            <th className="px-4 py-3">Last payment</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <SubsRow key={`${r.org_type}-${r.org_id}`} row={r} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubsRow({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  const quickToggle = async () => {
    const nextStatus = row.status === "disabled" ? "active" : "disabled";
    if (!window.confirm(`${nextStatus === "disabled" ? "Disable" : "Re-enable"} ${row.org_name}? ${nextStatus === "disabled" ? "Admins of this workspace will be blocked from logging in." : ""}`)) return;
    try {
      await api.put(`/admin/subscriptions/${row.org_type}/${row.org_id}`, {
        status: nextStatus, plan_name: row.plan_name, paid_until: row.paid_until,
        billing_cycle_months: row.billing_cycle_months, notes: row.notes,
      });
      toast.success(`${row.org_name} → ${nextStatus}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <tr className="border-t border-[var(--vs-border)]" data-testid={`subs-row-${row.org_id}`}>
      <td className="px-4 py-3">
        <div className="font-medium">{row.org_name}</div>
        <div className="text-xs text-[var(--vs-text-secondary)]">{row.location || "—"}</div>
      </td>
      <td className="px-4 py-3 font-mono text-xs">{row.plan_name || "—"}</td>
      <td className="px-4 py-3">
        <Badge variant="outline" className={STATUS_STYLES[row.status] || ""} data-testid={`subs-status-${row.org_id}`}>
          {row.status === "active" && <CheckCircle size={11} className="mr-1" weight="duotone" />}
          {row.status === "disabled" && <Warning size={11} className="mr-1" weight="duotone" />}
          {row.status}
        </Badge>
      </td>
      <td className="px-4 py-3 font-mono text-xs">{row.paid_until || "—"}</td>
      <td className="px-4 py-3 font-mono text-xs">{fmtINR(row.last_payment_amount)}</td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={quickToggle} data-testid={`subs-toggle-${row.org_id}`}>
            {row.status === "disabled" ? "Re-enable" : "Disable"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`subs-edit-${row.org_id}`}>Edit</Button>
            </DialogTrigger>
            <SubsEditDialog row={row} onSaved={() => { setOpen(false); onChanged?.(); }} />
          </Dialog>
        </div>
      </td>
    </tr>
  );
}

function SubsEditDialog({ row, onSaved }) {
  const [form, setForm] = useState({
    status: row.status,
    plan_name: row.plan_name || "",
    billing_cycle_months: row.billing_cycle_months || 12,
    paid_until: row.paid_until || "",
    last_payment_amount: row.last_payment_amount || "",
    notes: row.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/subscriptions/${row.org_type}/${row.org_id}`, {
        status: form.status,
        plan_name: form.plan_name || null,
        billing_cycle_months: Number(form.billing_cycle_months) || null,
        paid_until: form.paid_until || null,
        last_payment_amount: form.last_payment_amount ? Number(form.last_payment_amount) : null,
        notes: form.notes || null,
      });
      toast.success("Subscription saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <DialogContent>
      <DialogHeader><DialogTitle>{row.org_name} — subscription</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="subs-form-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Plan name</Label>
          <Input data-testid="subs-form-plan" value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} placeholder="monthly / yearly / pilot" />
        </div>
        <div>
          <Label>Billing cycle (months)</Label>
          <Input type="number" data-testid="subs-form-cycle" value={form.billing_cycle_months} onChange={(e) => setForm({ ...form, billing_cycle_months: e.target.value })} />
        </div>
        <div>
          <Label>Paid until</Label>
          <Input type="date" data-testid="subs-form-paid-until" value={form.paid_until} onChange={(e) => setForm({ ...form, paid_until: e.target.value })} />
        </div>
        <div>
          <Label>Last payment (₹)</Label>
          <Input type="number" data-testid="subs-form-amount" value={form.last_payment_amount} onChange={(e) => setForm({ ...form, last_payment_amount: e.target.value })} />
        </div>
        <div className="col-span-2">
          <Label>Notes</Label>
          <Input data-testid="subs-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
      </div>
      <Button onClick={submit} disabled={busy} data-testid="subs-form-save" className="mt-3 bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
        <FloppyDisk size={14} className="mr-1.5" /> Save
      </Button>
    </DialogContent>
  );
}
